import type { Dispatch } from 'react';

import { ELocationOuter, locationForReducer } from '~/src/features/videocontrol/data';
import {
    addCamera,
    decrementCameraCount,
    decrementCameraPriceWithCount,
    decrementInjector,
    decrementTotalCountCameras,
    incrementCameraCount,
    incrementCameraPriceWithCount,
    incrementInjector,
    incrementTotalCountCameras,
    removeCamera,
    resetCameras,
    resetInjector,
    resetTotalCountCameras,
    setLocationInstall,
    setOption,
    setPlan,
} from '~/src/features/videocontrol/Provider/actionCreators';
import type { IState, TActions, TCamera, TOption, TPlan } from '~/src/features/videocontrol/Provider/types';

const LAST_COUNT = 1;

export const addCameraDispatchWrap = (camera: TCamera, state: IState, dispatch: Dispatch<TActions>): void => {
    dispatch(addCamera(camera));
    dispatch(incrementTotalCountCameras(1));
    if (state.plan) {
        dispatch(setPlan({
            ...state.plan,
            priceMonthWithCount: state.plan.priceMonth * (state.totalCountCameras + 1),
        }));
    }
    if (camera.location in ELocationOuter) {
        dispatch(incrementInjector(1));
    }
    if (state.planOption) {
        dispatch(setOption({
            ...state.planOption,
            priceMonthWithCount: state.planOption.priceMonth * (state.totalCountCameras + 1),
        }));
    }
    if (state.installLocation) {
        dispatch(setLocationInstall({
            ...state.installLocation,
            priceTotalWithCount: state.installLocation.priceTotal * (state.totalCountCameras + 1),
        }));
    } else {
        dispatch(setLocationInstall(locationForReducer[camera.location]));
    }
};

export const removeCameraDispatchWrap = (currentCity: string, id: number, state: IState, dispatch: Dispatch<TActions>): void => {
    dispatch(removeCamera(id));
    dispatch(decrementTotalCountCameras(state.cameras[id].count));
    if (state.cameras[id].location in ELocationOuter) {
        dispatch(decrementInjector(state.cameras[id].count));
    }
    if (Object.keys(state.cameras).length === LAST_COUNT) {
        dispatch(setLocationInstall(null));
        dispatch(resetInjector(currentCity));
    } else if (state.installLocation) {
        dispatch(setLocationInstall({
            ...state.installLocation,
            priceTotalWithCount: state.installLocation.priceTotal * (state.totalCountCameras - state.cameras[id].count),
        }));
    }
    if (state.plan) {
        dispatch(setPlan({
            ...state.plan,
            priceMonthWithCount: state.plan.priceMonth * (state.totalCountCameras - state.cameras[id].count || 1),
        }));
    }
    if (state.planOption) {
        dispatch(setOption({
            ...state.planOption,
            priceMonthWithCount: state.planOption.priceMonth * (state.totalCountCameras - state.cameras[id].count || 1),
        }));
    }
};

export const resetCamerasDispatchWrap = (currentCity: string, dispatch: Dispatch<TActions>): void => {
    dispatch(resetCameras());
    dispatch(resetTotalCountCameras());
    dispatch(setLocationInstall(null));
    dispatch(resetInjector(currentCity));
};

export const incrementCameraCountDispatchWrap = (id: number, state: IState, dispatch: Dispatch<TActions>): void => {
    dispatch(incrementCameraCount({ id, currentCount: { ...state.cameras[id] }.count || 0 }));
    dispatch(incrementTotalCountCameras(1));
    if (state.cameras[id].location in ELocationOuter) {
        dispatch(incrementInjector(1));
    }
    if (state.cameras[id].count) {
        dispatch(incrementCameraPriceWithCount({
            id,
            priceTotal: Number({ ...state.cameras[id] }.priceTotal),
            priceMonth: Number({ ...state.cameras[id] }.priceMonth),
        }));
    }
    if (state.installLocation) {
        dispatch(setLocationInstall({
            ...state.installLocation,
            priceTotalWithCount: state.installLocation.priceTotal * (state.totalCountCameras + 1),
        }));
    }
    if (state.plan) {
        dispatch(setPlan({
            ...state.plan,
            priceMonthWithCount: state.plan.priceMonth * (state.totalCountCameras + 1),
        }));
    }
    if (state.planOption) {
        dispatch(setOption({
            ...state.planOption,
            priceMonthWithCount: state.planOption.priceMonth * (state.totalCountCameras + 1),
        }));
    }
};

export const decrementCameraCountDispatchWrap = (id: number, state: IState, dispatch: Dispatch<TActions>): void => {
    dispatch(decrementTotalCountCameras(1));
    if (state.cameras[id].location in ELocationOuter) {
        dispatch(decrementInjector(1));
    }
    if (state.plan) {
        dispatch(setPlan({
            ...state.plan,
            priceMonthWithCount: state.plan.priceMonth * (state.totalCountCameras - 1 || 1),
        }));
    }
    if (state.planOption) {
        dispatch(setOption({
            ...state.planOption,
            priceMonthWithCount: state.planOption.priceMonth * (state.totalCountCameras - 1 || 1),
        }));
    }
    if (state.cameras[id].count === LAST_COUNT) {
        dispatch(removeCamera(id));
        if (Object.keys(state.cameras).length === LAST_COUNT) {
            dispatch(setLocationInstall(null));
        }
        return;
    } else if (state.installLocation) {
        dispatch(setLocationInstall({
            ...state.installLocation,
            priceTotalWithCount: state.installLocation.priceTotal * (state.totalCountCameras - 1),
        }));
    }
    dispatch(decrementCameraPriceWithCount({
        id,
        priceTotal: Number({ ...state.cameras[id] }.priceTotal),
        priceMonth: Number({ ...state.cameras[id] }.priceMonth),
    }));
    dispatch(decrementCameraCount({ id, currentCount: { ...state.cameras[id] }.count }));
};

export const setPlanDispatchWrap = (plan: TPlan | null, state: IState, dispatch: Dispatch<TActions>): void => {
    if (plan) {
        dispatch(setPlan({
            ...plan,
            priceMonthWithCount: plan.priceMonth * (state.totalCountCameras || 1),
        }));
    } else {
        dispatch(setPlan(plan));
    }
    dispatch(setOption(null));
};

export const setOptionDispatchWrap = (option: TOption | null, state: IState, dispatch: Dispatch<TActions>): void => {
    if (option) {
        dispatch(setOption({
            ...option,
            priceMonthWithCount: option.priceMonth * (state.totalCountCameras || 1),
        }));
    } else {
        dispatch(setOption(option));
    }
};
